import { useChat } from '@ai-sdk/react';
import {
  DefaultChatTransport,
  type DynamicToolUIPart,
  getToolName,
  isTextUIPart,
  isToolUIPart,
  lastAssistantMessageIsCompleteWithApprovalResponses,
  type ToolUIPart,
  type UIMessage,
} from 'ai';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import ReactMarkdown from 'react-markdown';
import { MarkdownPrimitive } from '@almanac/catalog';
import rehypeSanitize from 'rehype-sanitize';
import {
  fitAssistantHistory,
  type AssistantPageContext,
  type FindingApproval,
} from '@almanac/contracts';
import { useAuth } from '../auth/AuthContext';
import { saveConversation, type StoredConversation } from '../lib/assistant-store';
import { notifyAlmanacDataChanged } from '../lib/data-events';
import type { AssistantSeed } from './assistant/AssistantProvider';

interface AssistantPanelProps {
  conversation: StoredConversation;
  groundingResearchId: string | null;
  pageContext?: AssistantPageContext;
  seed: AssistantSeed | null;
  /** per-user persistence scope; null skips persistence (identity unresolved) */
  storageScope: string | null;
  onSaved?: () => void;
  /**
   * Reports the live transcript length (including messages that are not
   * persisted yet, or at all) so the dock can enable "New conversation"
   * without waiting on storage.
   */
  onMessagesChanged?: (conversationId: string, count: number) => void;
}

function toolLabel(name: string): string {
  if (name === 'create_finding') return 'Save research item';
  return name.replaceAll('_', ' ');
}

function actionSummary(name: string, input: unknown): string {
  if (typeof input !== 'object' || input === null) return toolLabel(name);
  const values = input as Record<string, unknown>;
  if (name === 'save_feedback') {
    return `${String(values.action ?? 'update feedback')} on ${String(values.item_id ?? 'item')}`;
  }
  if (name === 'create_note') {
    return `Create ${String(values.kind ?? 'research')} note “${String(values.title ?? 'Untitled')}”`;
  }
  if (name === 'create_todo') {
    return `Create to-do “${String(values.title ?? 'Untitled')}”`;
  }
  if (name === 'create_finding') {
    return `Save research item “${String(values.title ?? 'Untitled')}” (topic ${String(values.topic ?? 'general')})`;
  }
  return toolLabel(name);
}

/** Reflect the saved audience, including a later explicit owner change. */
function findingAudienceLabel(visibility: unknown): string {
  if (visibility === 'private') return 'Private — only you';
  if (visibility === 'internal') return 'Internal — everyone signed into Almanac';
  if (visibility === 'public') return 'Public — anyone with the public link';
  return 'Open research item to check its audience';
}

/** Human review of the full generated summary before any write or audience change. */
function FindingPreview({
  part,
  approve,
}: {
  part: ToolUIPart | DynamicToolUIPart;
  approve: (id: string, approved: boolean, options?: FindingApproval) => void;
}) {
  const [visibility, setVisibility] = useState<'private' | 'internal'>('private');
  const [confirmed, setConfirmed] = useState(false);
  const values = part.input as Record<string, unknown>;
  const [topic, setTopic] = useState(String(values.topic ?? 'general'));
  const validTopic = /^[a-z0-9][a-z0-9-]{0,63}$/u.test(topic);
  if (part.state !== 'approval-requested') return null;
  return (
    <section className="space-y-3 rounded-lg border border-warning/35 bg-warning/10 p-3">
      <p className="font-semibold">Review research item: {String(values.title ?? 'Untitled')}</p>
      <p className="text-sm text-muted-foreground">
        Save this conversation’s claims, evidence, citations and open questions as a research item
        in Research. This keeps your original conversation separate; it does not create a to-do or
        run new research.
      </p>
      <div className="max-h-96 overflow-auto rounded-md bg-background p-3">
        <MarkdownPrimitive
          body={String(values.body_md ?? values.summary_md ?? '')}
          imageMode="placeholder"
        />
      </div>
      <p className="text-sm">{String(values.summary_md ?? '')}</p>
      <label className="block text-sm">
        Topic
        <input
          aria-label="Research item topic"
          value={topic}
          onChange={(event) => setTopic(event.target.value)}
          className="mt-1 block w-full rounded border bg-background p-2"
        />
      </label>
      <label className="block text-sm">
        Audience
        <select
          aria-label="Research item audience"
          value={visibility}
          onChange={(event) => {
            setVisibility(event.target.value as 'private' | 'internal');
            setConfirmed(false);
          }}
          className="mt-1 block w-full rounded border bg-background p-2"
        >
          <option value="private">Private — only you</option>
          <option value="internal">Internal — everyone signed into Almanac</option>
        </select>
      </label>
      {visibility === 'internal' ? (
        <label className="flex gap-2 text-sm">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          I approve sharing this entire summary with everyone signed into Almanac, including
          information from my private conversation or sources. Original sources and conversation
          remain private.
        </label>
      ) : null}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={!validTopic || (visibility === 'internal' && !confirmed)}
          onClick={() =>
            approve(part.approval.id, true, {
              tool_call_id: part.toolCallId,
              topic,
              visibility,
              share_confirmed: confirmed,
            })
          }
          className="rounded bg-primary px-3 py-2 text-primary-foreground disabled:opacity-40"
        >
          Save as research item
        </button>
        <button
          type="button"
          onClick={() => approve(part.approval.id, false)}
          className="rounded bg-muted px-3 py-2"
        >
          Cancel
        </button>
      </div>
    </section>
  );
}

function ToolActivity({
  part,
  approve,
}: {
  part: ToolUIPart | DynamicToolUIPart;
  approve: (id: string, approved: boolean, options?: FindingApproval) => void;
}) {
  const name = getToolName(part);
  if (part.state === 'approval-requested') {
    if (name === 'create_finding') return <FindingPreview part={part} approve={approve} />;
    return (
      <section className="space-y-3 rounded-lg border border-warning/35 bg-warning/10 p-3">
        <div>
          <p className="text-sm font-semibold text-foreground">Confirm action</p>
          <p className="text-sm text-muted-foreground">{actionSummary(name, part.input)}</p>
        </div>
        <pre className="max-h-36 overflow-auto rounded-md bg-background/70 p-2 text-xs whitespace-pre-wrap text-muted-foreground">
          {JSON.stringify(part.input, null, 2)}
        </pre>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => approve(part.approval.id, true)}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={() => approve(part.approval.id, false)}
            className="rounded-md bg-muted px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            Reject
          </button>
        </div>
      </section>
    );
  }

  if (
    name === 'create_finding' &&
    part.state === 'output-available' &&
    typeof part.output === 'object' &&
    part.output !== null
  ) {
    const result = part.output as Record<string, unknown>;
    if (
      result.saved === true &&
      typeof result.research_id === 'string' &&
      /^res_[a-zA-Z0-9_-]+$/u.test(result.research_id)
    ) {
      return (
        <p className="text-sm">
          Research item saved · {findingAudienceLabel(result.visibility)} ·{' '}
          <a className="text-primary underline" href={`/research/${result.research_id}`}>
            Open saved research item
          </a>
        </p>
      );
    }
    return (
      <p role="alert" className="text-sm text-destructive">
        Research item was not saved.{' '}
        {typeof result.error === 'string' ? result.error : 'Revise the preview and try again.'}
      </p>
    );
  }
  let state = 'Working…';
  if (part.state === 'output-available') state = 'Done';
  if (part.state === 'output-error') state = 'Failed';
  if (part.state === 'output-denied') state = 'Rejected';
  if (part.state === 'approval-responded')
    state = part.approval.approved ? 'Confirmed' : 'Rejected';
  return (
    <output className="flex items-center gap-2 text-xs text-muted-foreground">
      <span className="size-1.5 rounded-full bg-primary" aria-hidden="true" />
      <span className="capitalize">{toolLabel(name)}</span>
      <span>· {state}</span>
    </output>
  );
}

function Message({
  message,
  approve,
}: {
  message: UIMessage;
  approve: (id: string, approved: boolean, options?: FindingApproval) => void;
}) {
  return (
    <article
      className={
        message.role === 'user'
          ? 'ml-auto max-w-[88%] rounded-xl bg-muted px-3 py-2 text-sm text-foreground'
          : 'space-y-2 text-sm text-foreground'
      }
    >
      <span className="sr-only">{message.role === 'user' ? 'You' : 'Almanac'}</span>
      {message.parts.map((part, index) => {
        if (isTextUIPart(part)) {
          if (message.role === 'user') return <p key={index}>{part.text}</p>;
          return (
            <div key={index} className="assistant-markdown max-w-none text-foreground">
              <ReactMarkdown rehypePlugins={[rehypeSanitize]}>{part.text}</ReactMarkdown>
            </div>
          );
        }
        if (isToolUIPart(part)) {
          return <ToolActivity key={part.toolCallId} part={part} approve={approve} />;
        }
        return null;
      })}
    </article>
  );
}

/**
 * True when the newest message is an assistant turn that actually rendered
 * something — visible text or tool activity. A finished submit whose newest
 * message is still the user's, or whose assistant message carries no renderable
 * part, produced nothing for the reader to see.
 */
function assistantAnswered(messages: UIMessage[]): boolean {
  const last = messages.at(-1);
  if (!last || last.role !== 'assistant') return false;
  return last.parts.some((part) => {
    if (isTextUIPart(part)) return part.text.trim().length > 0;
    return isToolUIPart(part);
  });
}

/** Build the strict assistant request body and fit history only for the wire payload. */
function assistantRequestBody({
  id,
  trigger,
  messageId,
  messages,
  groundingResearchId,
  pageContext,
}: {
  id: string;
  trigger: 'submit-message' | 'regenerate-message';
  messageId: string | undefined;
  messages: UIMessage[];
  groundingResearchId: string | null;
  pageContext?: AssistantPageContext;
}): {
  id: string;
  trigger: 'submit-message' | 'regenerate-message';
  messageId?: string;
  messages: UIMessage[];
  page_context?: AssistantPageContext;
} {
  const body: {
    id: string;
    trigger: 'submit-message' | 'regenerate-message';
    messageId?: string;
    messages: UIMessage[];
    page_context?: AssistantPageContext;
  } = {
    id,
    trigger,
    messages: fitAssistantHistory(messages),
  };
  if (messageId !== undefined) body.messageId = messageId;
  if (pageContext) body.page_context = { ...pageContext };
  if (groundingResearchId) {
    body.page_context = { ...body.page_context, research_id: groundingResearchId };
  }
  return body;
}

/** Authenticated assistant conversation body for the global dock. */
export function AssistantPanel({
  conversation,
  groundingResearchId,
  pageContext,
  seed,
  storageScope,
  onSaved,
  onMessagesChanged,
}: AssistantPanelProps) {
  const { getToken } = useAuth();
  const groundingRef = useRef(groundingResearchId);
  const pageContextRef = useRef(pageContext);
  const scopeRef = useRef(storageScope);
  const savedMessagesRef = useRef<UIMessage[]>(conversation.messages);
  const lastSeedNonce = useRef<number | null>(null);
  const refreshedToolCalls = useRef(new Set<string>());
  const findingApprovals = useRef(new Map<string, FindingApproval>());
  const [input, setInput] = useState('');
  const [emptyResponse, setEmptyResponse] = useState(false);

  useEffect(() => {
    groundingRef.current = groundingResearchId;
    pageContextRef.current = pageContext;
    scopeRef.current = storageScope;
  }, [groundingResearchId, pageContext, storageScope]);

  const transport = useMemo(
    () =>
      new DefaultChatTransport<UIMessage>({
        api: '/api/assistant',
        headers: async (): Promise<Record<string, string>> => {
          const token = await getToken();
          return token ? { authorization: `Bearer ${token}` } : {};
        },
        prepareSendMessagesRequest: ({ id, messages, trigger, messageId }) => {
          const body = assistantRequestBody({
            id,
            trigger,
            messageId,
            messages,
            groundingResearchId: groundingRef.current,
            pageContext: pageContextRef.current,
          });
          const approvals = [...findingApprovals.current.values()].filter((entry) =>
            messages
              .at(-1)
              ?.parts.some((part) => isToolUIPart(part) && part.toolCallId === entry.tool_call_id),
          );
          // Old API pods reject unknown request fields. Keep ordinary Ask compatible;
          // saves retain their metadata and fail closed rather than use legacy sharing.
          if (approvals.length === 0) return { body };
          return { body: { ...body, finding_approvals: approvals } };
        },
      }),
    [getToken],
  );
  const {
    messages,
    sendMessage,
    status,
    error,
    stop,
    regenerate,
    addToolApprovalResponse,
    clearError,
  } = useChat({
    id: conversation.id,
    messages: conversation.messages,
    transport,
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
  });

  useEffect(() => {
    if (!seed || seed.nonce === lastSeedNonce.current) return;
    lastSeedNonce.current = seed.nonce;
    if (seed.question.trim()) void sendMessage({ text: seed.question });
  }, [seed, sendMessage]);

  useEffect(() => {
    onMessagesChanged?.(conversation.id, messages.length);
  }, [conversation.id, messages.length, onMessagesChanged]);

  useEffect(() => {
    savedMessagesRef.current = messages;
    if (messages.length === 0 || !storageScope) return;
    const timeout = window.setTimeout(() => {
      saveConversation(storageScope, {
        id: conversation.id,
        researchId: groundingRef.current ?? conversation.researchId,
        messages,
      });
      onSaved?.();
    }, 500);
    return () => window.clearTimeout(timeout);
  }, [conversation.id, conversation.researchId, messages, onSaved, storageScope]);

  // Latest-value refs for the teardown effect below, which must not re-run.
  const conversationRef = useRef(conversation);
  const onSavedRef = useRef(onSaved);
  const stopRef = useRef(stop);
  conversationRef.current = conversation;
  onSavedRef.current = onSaved;
  stopRef.current = stop;

  /**
   * Flush persistence and cancel streaming when the panel goes away. Everything
   * it touches is read through a ref so the effect mounts once and its cleanup
   * runs only on a real unmount.
   *
   * Depending on `conversation.researchId` here aborted live requests: a
   * grounded conversation has no stored researchId until its first persist, so
   * the value flips from undefined to the research id mid-request, re-running
   * the effect and calling stop() on the in-flight stream. The server saw a
   * cancelled stream and logged a clean, empty `assistant.finished`; the reader
   * saw the panel go idle with no answer.
   */
  useEffect(() => {
    function flush() {
      if (savedMessagesRef.current.length === 0 || !scopeRef.current) return;
      saveConversation(scopeRef.current, {
        id: conversationRef.current.id,
        researchId: groundingRef.current ?? conversationRef.current.researchId,
        messages: savedMessagesRef.current,
      });
      onSavedRef.current?.();
    }

    function onVisibilityChange() {
      if (document.visibilityState === 'hidden') flush();
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      flush();
      void stopRef.current();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    for (const message of messages) {
      for (const part of message.parts) {
        if (!isToolUIPart(part) || part.state !== 'output-available') continue;
        const name = getToolName(part);
        if (
          name !== 'save_feedback' &&
          name !== 'create_note' &&
          name !== 'create_todo' &&
          name !== 'create_finding'
        )
          continue;
        if (refreshedToolCalls.current.has(part.toolCallId)) continue;
        refreshedToolCalls.current.add(part.toolCallId);
        notifyAlmanacDataChanged(name);
      }
    }
  }, [messages]);

  const busy = status === 'streaming' || status === 'submitted';
  const wasBusy = useRef(false);
  const stoppedByUser = useRef(false);

  /**
   * Watch a submit through to completion. A run that ends with nothing rendered
   * is otherwise indistinguishable from success — the panel simply goes idle —
   * so record it and surface the same failure box a thrown error gets.
   */
  useEffect(() => {
    if (busy) {
      wasBusy.current = true;
      stoppedByUser.current = false;
      setEmptyResponse(false);
      return;
    }
    if (!wasBusy.current || status !== 'ready') return;
    wasBusy.current = false;
    // Cancelling on purpose is not a failure to report back.
    if (stoppedByUser.current) {
      stoppedByUser.current = false;
      return;
    }
    setEmptyResponse(!assistantAnswered(messages));
  }, [busy, messages, status]);

  function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    clearError();
    setEmptyResponse(false);
    setInput('');
    void sendMessage({ text: trimmed });
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    send(input);
  }

  let failureMessage: string | null = null;
  if (error) {
    failureMessage = error.message || 'The assistant could not finish that response.';
  } else if (emptyResponse) {
    failureMessage = 'Almanac returned an empty response.';
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex-1 space-y-4 overflow-y-auto p-4" aria-live="polite">
        {messages.map((message) => (
          <Message
            key={message.id}
            message={message}
            approve={(id, approved, options) => {
              if (options) findingApprovals.current.set(options.tool_call_id, options);
              void addToolApprovalResponse({ id, approved });
            }}
          />
        ))}
        {busy ? (
          <output className="block text-sm text-muted-foreground">Almanac is working…</output>
        ) : null}
        {failureMessage ? (
          <div className="space-y-2 rounded-lg bg-danger/10 p-3 text-sm text-danger" role="alert">
            <p>{failureMessage}</p>
            <button type="button" onClick={() => void regenerate()} className="underline">
              Retry
            </button>
          </div>
        ) : null}
      </div>
      <form onSubmit={submit} className="space-y-2 border-t border-border/70 p-3">
        <label className="sr-only" htmlFor="assistant-follow-up">
          Ask a follow-up
        </label>
        <div className="flex gap-2">
          <textarea
            id="assistant-follow-up"
            rows={2}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send(input);
              }
            }}
            placeholder="Ask a follow-up…"
            className="min-w-0 flex-1 resize-none rounded-lg bg-background px-3 py-2 text-base text-foreground ring-1 ring-foreground/10 outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-sm"
          />
          {busy ? (
            <button
              type="button"
              onClick={() => {
                stoppedByUser.current = true;
                void stop();
              }}
              className="rounded-lg bg-muted px-3 text-sm"
            >
              Stop
            </button>
          ) : (
            <button
              type="submit"
              disabled={!input.trim()}
              className="rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground disabled:opacity-40"
            >
              Send
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={busy || messages.length === 0}
            onClick={() =>
              send(
                'Prepare a research item for my review: a standalone summary of the claims, evidence, citations and open questions from this conversation. Use create_finding to show the preview, with a fitting topic. Keep my original conversation separate.',
              )
            }
            className="rounded-md px-2 py-1 text-sm text-primary hover:bg-primary/10 disabled:opacity-40"
          >
            Save as research item
          </button>
          <p className="text-xs text-muted-foreground">
            Preview a standalone summary, then choose its topic and audience before saving to
            Research.
          </p>
        </div>
      </form>
    </div>
  );
}
